import { createMemo, createSignal, For, type JSX, Show } from 'solid-js';
import { t } from '../../i18n';
import { jobName } from '../../lib/card-controls';
import { tagOfJob, useCardControls } from '../../lib/card-controls-context';
import { pressMove, useCardMove } from '../../lib/card-move';
import type { UtilBox } from '../../lib/fleetUtilization';
import {
  boxJobs,
  JOB_TAG_LABEL,
  jobGroups,
  jobSubLine,
  jobTitle,
  jobUsageView,
  summaryLine,
} from '../../lib/machine-jobs';
import { jobGrippable, jobSubject } from '../../lib/move-items';
import { ControlNotices } from './ControlNotices';
import { JobControls } from './JobControls';
import { JobGroup } from './JobGroup';
import { JobUsageRow } from './JobUsageRow';
import { MoveButton } from './MoveButton';
import { MoveGrip } from './MoveGrip';
import { MoveTag } from './MoveTag';
import { MoveToMenu } from './MoveToMenu';

// Every job a machine holds for this account, in the card's Now zone (#6745, prototype
// jobsHTML, `:2180-2225`). One job: the large layout, its title, "<repo> · for <duration>"
// and its usage row. Two or more: one group per capability in first-seen order, each
// foldable, each job a row that opens its details. Folded by the zone's toggle (`folded`),
// one summary line, "Coder ×2 · Reviewer". Fold state is this card's, in memory, not saved.
//
// The job controls (#6753) show for an admin on this org's machine only
// (`useCardControls().on()`): one job carries labelled Pause or Resume and Stop buttons
// (prototype jobCtl, labelled), two or more carry icon buttons on each row (`JobRow`). A
// viewer, a member or another org's machine sees the same list with no control. The Move grip
// (#6771) sits before a movable job, with "Move" (labelled, one job) or the swap icon (a row),
// and Move to opens under it. The Needs you / Blocked tags are a later slice (#6772).

export function MachineJobs(props: {
  readonly box: UtilBox;
  readonly nowMs: number;
  readonly folded: boolean;
  readonly listId: string;
}): JSX.Element {
  const [closedGroups, setClosedGroups] = createSignal<ReadonlySet<string>>(new Set());
  const [openJobs, setOpenJobs] = createSignal<ReadonlySet<string>>(new Set());
  const flip = (set: ReadonlySet<string>, key: string): ReadonlySet<string> => {
    const next = new Set(set);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  };
  const jobs = () => boxJobs(props.box);
  const groups = createMemo(() => jobGroups(props.box, t));
  const one = () => (jobs().length === 1 ? (jobs()[0] ?? null) : null);
  const controls = useCardControls();
  const move = useCardMove();
  /** The one job's move item, when the person may move it and it can be dragged. */
  const oneSubject = createMemo(() => {
    const job = one();
    if (job === null || move === null || !move.on()) return null;
    return jobGrippable(props.box, job, move.byTask()) ? jobSubject(props.box, job, t) : null;
  });
  return (
    <div id={props.listId} class="mission-jobs">
      <Show
        when={one()}
        fallback={
          <Show
            when={!props.folded}
            fallback={<p class="mission-jobs__summary">{summaryLine(props.box, t)}</p>}
          >
            <div class="mission-jobs__groups">
              {/* Keyed by capability key, looked up in the current read: the 20-second poll
                  builds new group objects, and iterating them would re-create every button
                  and drop a keyboard user's focus (`MachineCardGrid` keys by id likewise). */}
              <For each={groups().map((g) => g.key)}>
                {(key) => (
                  <Show when={groups().find((g) => g.key === key)}>
                    {(group) => (
                      <JobGroup
                        group={group()}
                        box={props.box}
                        nowMs={props.nowMs}
                        closed={closedGroups().has(key)}
                        onToggle={() => setClosedGroups((s) => flip(s, key))}
                        isOpen={(id) => openJobs().has(id)}
                        onToggleJob={(id) => setOpenJobs((s) => flip(s, id))}
                      />
                    )}
                  </Show>
                )}
              </For>
            </div>
          </Show>
        }
      >
        {(job) => (
          <div
            class="mission-jobs__one"
            data-move-row={oneSubject()?.key}
            data-move-task={job().id}
            onPointerDown={(e) => {
              const subject = oneSubject();
              if (move !== null && subject !== null) pressMove(move, subject, e);
            }}
          >
            <Show when={oneSubject()}>{(subject) => <MoveGrip subject={subject()} />}</Show>
            <p class="mission-card__now" data-job="">
              {jobTitle(job(), t)}
            </p>
            <Show when={jobSubLine(job(), props.nowMs, t)}>
              {(sub) => <p class="mission-card__now-sub">{sub()}</p>}
            </Show>
            <JobUsageRow view={jobUsageView(job(), props.box, t)} />
            <Show when={tagOfJob(controls, job())}>
              {(tg) => (
                <span
                  class="mission-jobs__tag"
                  classList={{ 'mission-jobs__tag--live': tg() === 'starting' }}
                  data-tag={tg()}
                >
                  {t(JOB_TAG_LABEL[tg()])}
                </span>
              )}
            </Show>
            <MoveTag taskId={job().id} />
            <Show when={oneSubject()} fallback={<JobControls job={job()} labelled />}>
              {(subject) => (
                <span class="move-one__actions">
                  <JobControls job={job()} labelled />
                  <MoveButton subject={subject()} labelled />
                </span>
              )}
            </Show>
            <Show when={oneSubject()}>{(subject) => <MoveToMenu subject={subject()} />}</Show>
            <ControlNotices
              kind="job"
              id={job().id}
              name={jobName(job())}
              onConfirm={() => controls?.card.confirmJobStop(job())}
            />
          </div>
        )}
      </Show>
    </div>
  );
}
